import { Alert, Box, Button, CircularProgress, Stack, Typography } from "@mui/material";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getApiErrorMessage } from "../../../modules/common/shared/util/ApiErrorUtil";
import { PLACEHOLDER_SCHOOL, ReceiptModel, SchoolHeader } from "../../fees/receipt/receiptModel";
import { renderReceiptPdf } from "../../fees/receipt/receiptService";
import { SettingsCard } from "../components/SettingsCard";
import { SETTINGS_ROUTE } from "../settingsSections";
import { SchoolProfile, fetchSchoolLogo, fetchSchoolProfile } from "../schoolProfile/schoolProfileService";

// A made-up receipt that shows every part of the template: terms, a concession, a term balance
const SAMPLE_RECEIPT: ReceiptModel = {
  receiptNo: 1001,
  receiptDate: "15-06-2026",
  student: { name: "Sample Student", admissionNo: "ADM001", className: "Five - A", academicYear: "2026-27" },
  items: [{ description: "Tuition Fee", terms: "1, 2, 3", due: 27000, concession: 1000, paid: 20000 }],
  payment: { mode: "UPI", transactionId: "TXN0000001", date: "15-06-2026", balance: { label: "Term 3 Balance", amount: 6000 } },
  totalPaid: 20000,
  signedBy: { label: "Received By", name: "Office" },
};

const toHeader = (p: SchoolProfile, logo: string | null): SchoolHeader => {
  const place = [p.addressLine1, p.addressLine2, p.city, p.state].filter(Boolean).join(", ");
  return {
    name: p.name,
    address: `${place} - ${p.pincode}`,
    contact: [p.phone, p.email].filter(Boolean).join(" · ") || undefined,
    logo: logo ?? undefined,
  };
};

const canPreviewPdf = (): boolean => (navigator as Navigator & { pdfViewerEnabled?: boolean }).pdfViewerEnabled ?? true;

/** Settings → Fees & Receipts: a sample receipt drawn with the real template and the saved School Profile. */
export const ReceiptPreviewSection = () => {
  const [url, setUrl] = useState<string | null>(null);
  const [usingPlaceholders, setUsingPlaceholders] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const build = useCallback(async () => {
    setError(null);
    setUrl(null);
    try {
      const profile = await fetchSchoolProfile();
      const logo = profile?.hasLogo ? await fetchSchoolLogo() : null;
      setUsingPlaceholders(!profile);
      const blob = await renderReceiptPdf(SAMPLE_RECEIPT, profile ? toHeader(profile, logo) : PLACEHOLDER_SCHOOL);
      setUrl(URL.createObjectURL(blob));
    } catch (err) {
      setError(getApiErrorMessage(err, "Couldn't build the sample receipt."));
    }
  }, []);

  useEffect(() => {
    build();
  }, [build]);

  useEffect(
    () => () => {
      if (url) URL.revokeObjectURL(url);
    },
    [url]
  );

  const download = () => {
    if (!url) return;
    const link = document.createElement("a");
    link.href = url;
    link.download = "Sample_Receipt.pdf";
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  return (
    <Stack spacing={2.5}>
      {usingPlaceholders && (
        <Alert
          severity="info"
          sx={{ borderRadius: 2 }}
          action={
            <Button component={Link} to={`${SETTINGS_ROUTE}/school`} color="inherit" size="small" startIcon={<EditRoundedIcon />} sx={{ fontWeight: 600 }}>
              School Profile
            </Button>
          }
        >
          The school details are placeholders until the School Profile is saved.
        </Alert>
      )}

      <SettingsCard
        icon={<ReceiptLongRoundedIcon />}
        title="Receipt preview"
        description="A sample fee receipt, exactly as parents and students get it. The student and payment details are made up."
        action={
          url && (
            <Button variant="outlined" size="small" startIcon={<DownloadRoundedIcon />} onClick={download} sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2, flexShrink: 0 }}>
              Download
            </Button>
          )
        }
      >
        {error ? (
          <Alert
            severity="error"
            sx={{ borderRadius: 2 }}
            action={
              <Button color="inherit" size="small" onClick={build} sx={{ fontWeight: 600 }}>
                Retry
              </Button>
            }
          >
            {error}
          </Alert>
        ) : !url ? (
          <Stack spacing={1.5} alignItems="center" sx={{ py: 10 }}>
            <CircularProgress size={28} />
            <Typography sx={{ fontSize: 13.5, color: "text.secondary" }}>Preparing the sample receipt…</Typography>
          </Stack>
        ) : canPreviewPdf() ? (
          <Box
            component="iframe"
            title="Sample receipt"
            src={`${url}#toolbar=0&navpanes=0&view=FitH`}
            sx={{ width: "100%", height: { xs: 520, md: 720 }, border: "1px solid #e5e7eb", borderRadius: 2, bgcolor: "#f1f5f9" }}
          />
        ) : (
          <Stack spacing={1.5} alignItems="center" sx={{ py: 6, textAlign: "center" }}>
            <Typography sx={{ fontWeight: 600 }}>This device can't show PDFs inline</Typography>
            <Button variant="contained" startIcon={<DownloadRoundedIcon />} onClick={download} sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}>
              Download the sample receipt
            </Button>
          </Stack>
        )}
      </SettingsCard>
    </Stack>
  );
};

export default ReceiptPreviewSection;
